import React, { useCallback } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUp } from "@fortawesome/free-solid-svg-icons";
import { Routes } from "app/route/RoutesConfig";
import { userSelector } from "slices/user/userSlice";
import { DispatchManifestStopDetailStatus } from "app/data/city-delivery/dispatch-board/models";
import type { PickupGroupBlock } from "../hooks/usePickupAssignmentDriversColumn";
import { useNestedExpandSync } from "../driverColumnNestExpandContext";
import { ReactComponent as Arrow } from "images/arrow-down_gray.svg";
import "./pickupGroupCard.scss";

const groupStatusClass: Record<DispatchManifestStopDetailStatus, string> = {
  [DispatchManifestStopDetailStatus.COMPLETED]: "pa-pickup-group__tag--completed",
  [DispatchManifestStopDetailStatus.IN_PROGRESS]: "pa-pickup-group__tag--in-progress",
  [DispatchManifestStopDetailStatus.PENDING]: "pa-pickup-group__tag--pending",
  [DispatchManifestStopDetailStatus.EXCEPTIONS]: "pa-pickup-group__tag--exceptions",
};

function titleCaseWords(s: string): string {
  return s
    .split("_")
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(" ");
}

function groupStatusLabel(s: DispatchManifestStopDetailStatus): string {
  return titleCaseWords(s);
}

type Props = {
  task: PickupGroupBlock;
  onSelectPickup: (pickupNumber: string) => void;
};

export const PickupGroupCard: React.FC<Props> = ({ task, onSelectPickup }) => {
  const terminalId = useSelector(userSelector).activeTerminal?.id;
  const probillHref = useCallback(
    (probillNumber: number) => {
      const prefix = terminalId != null ? `/${terminalId}` : "";
      return `${prefix}${Routes.shipments.tracking}/${probillNumber}`;
    },
    [terminalId]
  );

  const [open, setOpen] = useNestedExpandSync();

  const pickupCountLabel = `${task.pickups.length} ${
    task.pickups.length === 1 ? "pickup" : "pickups"
  }`;

  return (
    <div className="pa-pickup-group">
      <button
        type="button"
        className={`pa-pickup-group__head ${!open ? "pa-pickup-group__head--single-line" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <Arrow
          className="pa-pickup-group__chevron"
          width={8}
          height={12}
          color="#828282"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        />
        <div className="pa-pickup-group__head-main">
          {!open ? (
            <div className="pa-pickup-group__collapsed-row">
              <span className="pa-pickup-group__address pa-pickup-group__address--collapsed">
                {task.shipperAddress}
              </span>
              <span className="pa-pickup-group__stat-tags">
                <span className="pa-pickup-group__type-tag pa-pickup-group__type-tag--pickup">
                  <FontAwesomeIcon icon={faArrowUp} className="pa-pickup-group__fa-icon" />
                  {`${task.completedPickups}/${task.totalPickups}`}
                </span>
              </span>
            </div>
          ) : (
            <>
              <div className="pa-pickup-group__line1">
                <span className="pa-pickup-group__address">{task.shipperAddress}</span>
                <span
                  className={`pa-pickup-group__tag ${groupStatusClass[task.groupStopStatus] ?? ""}`}
                >
                  {groupStatusLabel(task.groupStopStatus)}
                </span>
              </div>
              <div className="pa-pickup-group__line2">
                <span className="pa-pickup-group__type-tag pa-pickup-group__type-tag--pickup">
                  <FontAwesomeIcon icon={faArrowUp} className="pa-pickup-group__fa-icon" />
                  Pickup
                </span>
                <span className="pa-pickup-group__stats">{pickupCountLabel}</span>
              </div>
            </>
          )}
        </div>
      </button>

      {open && (
        <ul className="pa-pickup-group__list">
          {task.pickups.map((pickup) => (
            <li key={pickup.id} className="pa-pickup-group__item">
              <div className="pa-pickup-group__row">
                <span className="pa-pickup-group__label">Pickup #</span>
                <button
                  type="button"
                  className="pa-pickup-group__link-btn"
                  onClick={() => onSelectPickup(pickup.pickupNumber)}
                >
                  {pickup.pickupNumber}
                </button>
              </div>
              <div className="pa-pickup-group__row">
                <span className="pa-pickup-group__label">PRO#</span>
                {pickup.probillNumber != null ? (
                  <a
                    className="pa-pickup-group__external-link"
                    href={probillHref(pickup.probillNumber)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {pickup.probillNumber}
                  </a>
                ) : (
                  <span>—</span>
                )}
              </div>
              <div className="pa-pickup-group__row">
                <span className="pa-pickup-group__label">Shipper</span>
                <span>{pickup.shipperName}</span>
              </div>
              <div className="pa-pickup-group__row">
                <span className="pa-pickup-group__label">Assigned</span>
                <span>{pickup.assignedRelative}</span>
              </div>
              <div className="pa-pickup-group__row">
                <span className="pa-pickup-group__label">Status</span>
                <span className="pa-pickup-group__status-pill">{pickup.status}</span>
              </div>
              <div className="pa-pickup-group__row">
                <span className="pa-pickup-group__label">Pieces / Weight</span>
                <span>
                  {pickup.pieces} pcs · {pickup.weight} lbs
                </span>
              </div>
              <div className="pa-pickup-group__row">
                <span className="pa-pickup-group__label">Pickup window</span>
                <span>{pickup.timeRange}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
